import {
  generatePublicS3ItemPreSignedUrlV1,
  generateS3ItemPreSignedUrlV1,
} from "@/lib/data/requests/s3";
import { queryKeys } from "@/lib/data/static";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import type { PdfEditorFileType } from "./PdfEditor";

const getPdfEditorFallbackFileTypes = (
  fileType: PdfEditorFileType,
): PdfEditorFileType[] => {
  if (fileType === "matter-file") {
    return ["matter-file", "matter-signature-request-file"];
  }

  return [fileType];
};

export function usePdfEditorDocumentUrl({
  fileType,
  initialDocUrl,
  presignedUrlKey,
  unauthenticated = false,
}: {
  fileType: PdfEditorFileType;
  initialDocUrl?: string;
  presignedUrlKey?: string;
  unauthenticated?: boolean;
}) {
  const presignedObjectUrlRef = useRef<string | null>(null);
  const [presignedObjectUrl, setPresignedObjectUrl] = useState<string | null>(
    null,
  );
  const [isPreparingDocumentUrl, setIsPreparingDocumentUrl] = useState(false);
  const [didPresignedBlobFetchFail, setDidPresignedBlobFetchFail] =
    useState(false);

  const {
    data: presignedUrl,
    isLoading: isLoadingFileUrl,
    isError: isErrorFileUrl,
  } = useQuery({
    queryKey: [
      queryKeys.matterFileImages,
      presignedUrlKey,
      "pdf-editor",
      unauthenticated ? "public" : "auth",
    ],
    enabled: Boolean(presignedUrlKey) && !initialDocUrl,
    refetchOnMount: "always",
    staleTime: 0,
    gcTime: 0,
    queryFn: async () => {
      if (!presignedUrlKey) {
        return "";
      }
      const candidateFileTypes = getPdfEditorFallbackFileTypes(fileType);
      const generatePresignedUrl = unauthenticated
        ? generatePublicS3ItemPreSignedUrlV1
        : generateS3ItemPreSignedUrlV1;
      let lastError: unknown = null;

      for (const candidateFileType of candidateFileTypes) {
        try {
          const response = await generatePresignedUrl(candidateFileType)({
            clientMethod: "get_object",
            key: presignedUrlKey,
          });
          return response.data.presignedUrl;
        } catch (error) {
          lastError = error;
        }
      }

      throw lastError ?? new Error("Unable to prepare PDF URL.");
    },
  });

  useEffect(() => {
    if (initialDocUrl || !presignedUrl) {
      if (presignedObjectUrlRef.current) {
        URL.revokeObjectURL(presignedObjectUrlRef.current);
        presignedObjectUrlRef.current = null;
      }
      setPresignedObjectUrl(null);
      setIsPreparingDocumentUrl(false);
      setDidPresignedBlobFetchFail(false);
      return;
    }

    let isActive = true;
    setIsPreparingDocumentUrl(true);
    setDidPresignedBlobFetchFail(false);

    void fetch(presignedUrl)
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`Failed to fetch PDF (${response.status})`);
        }

        const blob = await response.blob();
        if (!isActive) return;

        const nextObjectUrl = URL.createObjectURL(blob);
        if (presignedObjectUrlRef.current) {
          URL.revokeObjectURL(presignedObjectUrlRef.current);
        }

        presignedObjectUrlRef.current = nextObjectUrl;
        setPresignedObjectUrl(nextObjectUrl);
      })
      .catch((error: unknown) => {
        console.error("Failed to prepare PDF object URL", error);
        if (!isActive) return;
        setDidPresignedBlobFetchFail(true);
        setPresignedObjectUrl(null);
      })
      .finally(() => {
        if (!isActive) return;
        setIsPreparingDocumentUrl(false);
      });

    return () => {
      isActive = false;
    };
  }, [initialDocUrl, presignedUrl]);

  useEffect(() => {
    return () => {
      if (presignedObjectUrlRef.current) {
        URL.revokeObjectURL(presignedObjectUrlRef.current);
        presignedObjectUrlRef.current = null;
      }
    };
  }, []);

  const resolvedDocUrl =
    initialDocUrl ??
    presignedObjectUrl ??
    (didPresignedBlobFetchFail ? (presignedUrl ?? "") : "");

  return {
    hasDocumentSource: Boolean(resolvedDocUrl),
    isErrorFileUrl,
    isLoadingFileUrl,
    isPreparingDocumentUrl,
    resolvedDocUrl,
  };
}
